'use client'

import { Capacitor } from '@capacitor/core'
import { useOrgContext } from '@dolfin/hooks/state/organization'
import { t } from '@lingui/core/macro'
import { User01, Users01 } from '@untitled-ui/icons-react'
import React, { CSSProperties, useState } from 'react'
import {
  Button,
  ButtonProps as RACButtonProps,
  TooltipTrigger
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import Badge, { BadgeResponsiveContainer, BadgeSizes } from './Badge'
import Tooltip from './Tooltip'

export type AvatarSizes = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | '3xl'

interface AvatarProps extends RACButtonProps {
  size?: AvatarSizes
  label?: string
  imgUrl?: string
  name?: string
  email?: string
  withTooltip?: boolean
  showDetails?: boolean
  onlyShowEmailInTooltip?: boolean
  highlighted?: boolean
  onBrand?: boolean
  isLink?: boolean
  interactive?: boolean
  showTeamAsBadge?: boolean
  userId?: string
  teams?: string[]
  userType?: string
  deactivated?: boolean
}

const textStyles = tv({
  base: "uppercase text-center font-medium font-['Inter']",
  variants: {
    size: {
      xs: 'text-xs leading-[18px]',
      s: 'text-sm leading-tight',
      m: 'text-base leading-normal',
      l: 'text-lg leading-7',
      xl: 'text-xl leading-[30px]',
      xxl: 'text-2xl leading-loose',
      '3xl': 'text-3xl leading-[45px]'
    },
    isDeactivated: {
      true: 'text-neutral-300'
    },
    onBrand: {
      true: 'text-neutral-100',
      false: 'text-neutral-900',
      undefined: 'text-neutral-900'
    }
  },
  compoundVariants: [
    {
      onBrand: false,
      isDeactivated: true,
      className: 'text-neutral-500'
    }
  ]
})

const nameStyles = tv({
  base: "block truncate ... self-stretch w-full font-semibold font-['Plus Jakarta Sans'] leading-tight flex items-center",
  variants: {
    size: {
      xs: 'text-xs',
      s: 'text-sm',
      m: 'text-base',
      l: 'text-lg',
      xl: 'text-xl',
      xxl: 'text-2xl',
      '3xl': 'text-3xl'
    },
    onBrand: {
      true: 'text-neutral-100',
      false: 'text-neutral-700',
      undefined: 'text-neutral-700'
    }
  }
})

const styles = tv({
  base: 'overflow-hidden rounded-full outline-none flex items-center justify-center gap-2 shrink-0',
  variants: {
    highlighted: {
      true: 'ring-warning-400',
      false: 'ring-neutral-0'
    },
    imgUrl: {
      false: 'bg-neutral-50 border border-neutral-300'
    },
    size: {
      xs: 'w-6 h-6 ring-[1px] shadow',
      s: 'w-8 h-8 ring-[1px] shadow',
      m: 'w-10 h-10 ring-[1px] shadow',
      l: 'w-12 h-12 ring-[1px] shadow',
      xl: 'w-14 h-14 ring-[2px] shadow-lg',
      xxl: 'w-16 h-16 ring-[2px] shadow-lg',
      '3xl': 'w-20 h-20 ring-[3px] shadow-xl'
    },
    onPress: {
      true: 'cursor-pointer'
    },
    onBrand: {
      true: 'bg-neutral-800',
      false: 'bg-neutral-50',
      undefined: 'bg-neutral-50'
    }
  },
  compoundVariants: [
    { highlighted: true, size: 'xs', className: 'ring-[2px]' },
    { highlighted: true, size: 's', className: 'ring-[2px]' },
    { highlighted: true, size: 'm', className: 'ring-[3px]' },
    { highlighted: true, size: 'l', className: 'ring-[3px]' },
    { highlighted: true, size: 'xl', className: 'ring-[4px]' },
    { highlighted: true, size: 'xxl', className: 'ring-[4px]' },
    { highlighted: true, size: '3xl', className: 'ring-[5px]' }
  ]
})

const containerStyles = tv({
  base: 'flex focus:outline-none w-full',
  variants: {
    isLink: {
      true: 'border border-neutral-200 pl-2 pr-4 py-1.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] cursor-pointer bg-neutral-0 hover:bg-neutral-50'
    },
    alignStart: {
      true: 'items-start',
      false: 'items-center'
    },
    deactivated: {
      true: 'opacity-70',
      false: ''
    },
    size: {
      xs: 'gap-1',
      s: 'gap-1',
      m: 'gap-2',
      l: 'gap-2',
      xl: ' gap-4',
      xxl: ' gap-4',
      '3xl': ' gap-6'
    }
  }
})

const teamsStyles = tv({
  base: 'text-xs flex items-center gap-1',
  variants: {
    onBrand: {
      true: 'text-neutral-300',
      false: 'text-neutral-500',
      undefined: 'text-neutral-500'
    }
  }
})

const detailsContainerStyles = tv({
  base: 'flex-col text-left justify-start items-start overflow-hidden flex',
  variants: {
    size: {
      xs: 'ml-1',
      s: 'ml-2',
      m: 'ml-3',
      l: 'ml-3',
      xl: 'ml-3',
      xxl: 'ml-4',
      '3xl': 'ml-4'
    }
  }
})

const Avatar = ({
  showDetails = true,
  size = 'm',
  interactive = true,
  ...props
}: AvatarProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [imageError, setImageError] = useState(false)

  const isCapacitor = Capacitor.isNativePlatform()

  const onClick = props.isLink
    ? async () => {
        if (!isCapacitor && props.userId) {
          const href = `/dashboard?page=user&id=${props.userId}`
          try {
            const { default: Router } = await import('next/router')
            await Router.push(href)
          } catch {
            window.location.assign(href)
          }
        } else {
          console.log('Capacitor navigation should be handled here.')
        }
      }
    : undefined

  const onBtnPress = () => {
    containerRef.current?.click()
  }

  const avatarContent = (
    <div
      style={props.style as CSSProperties}
      className={twMerge(
        props.className as string,
        styles({
          highlighted: !!props.highlighted,
          size,
          imgUrl: !!props.imgUrl && !imageError,
          onPress: !!props.onPress || props.isLink,
          onBrand: !!props.onBrand
        })
      )}>
      {props.imgUrl && !imageError && (
        <img
          src={props.imgUrl}
          alt={props.label}
          className={`object-cover w-full h-full ${
            !!props.deactivated ? 'filter grayscale' : ''
          }`}
          onError={() => setImageError(true)}
        />
      )}

      {(!props.imgUrl || imageError) && props.label && (
        <p
          className={textStyles({
            size,
            isDeactivated: !!props.deactivated,
            onBrand: !!props.onBrand
          })}>
          {props.label.substring(0, 2)}
        </p>
      )}
    </div>
  )

  const avatarWithDetails = (
    <>
      {avatarContent}
      {(props.name || props.email) && showDetails && (
        <div className={detailsContainerStyles({ size })}>
          {props.name && (
            <div className='flex items-center'>
              <p
                className={nameStyles({
                  size,
                  onBrand: !!props.onBrand
                })}>
                {props.name}
              </p>
              {props.userType && (
                <Badge className='ml-2' size={BadgeSizes.xs}>
                  {props.userType}
                </Badge>
              )}
              {props.deactivated && (
                <Badge color='neutral' size={BadgeSizes.xs} className='ml-2'>
                  {t`Deactivated`}
                </Badge>
              )}
            </div>
          )}
          {props.email && !props.onlyShowEmailInTooltip && (
            <p
              className={twMerge(
                "block truncate ... self-stretch w-full text-xs font-medium font-['Plus Jakarta Sans'] leading-tight mb-1",
                props.onBrand ? 'text-neutral-100' : 'text-neutral-700'
              )}>
              {props.email}
            </p>
          )}

          {props.teams && (
            <div className='relative w-full flex items-center gap-1 justify-start mt-0.5'>
              {!props.showTeamAsBadge ? (
                <p className={teamsStyles({ onBrand: !!props.onBrand })}>
                  {props.teams.length > 1 ? (
                    <Users01 height={12} width={12} />
                  ) : (
                    <User01 height={12} width={12} />
                  )}
                  <span>
                    {props.teams.length > 1
                      ? t`${props.teams.length} Teams`
                      : props.teams}
                  </span>
                </p>
              ) : (
                <BadgeResponsiveContainer>
                  <Badge size={BadgeSizes.xs}>{props.teams.join(', ')}</Badge>
                </BadgeResponsiveContainer>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )

  const renderedAvatar = interactive ? (
    <TooltipTrigger
      isDisabled={!props.withTooltip || !(props.name || props.email)}
      delay={300}>
      <Button
        onPress={onBtnPress}
        className={containerStyles({
          isLink: !!props.isLink && !!props.userId,
          alignStart:
            showDetails &&
            !!(
              (props.email &&
                props.email != '' &&
                !props.onlyShowEmailInTooltip) ||
              (props.teams && props.teams.length > 0)
            ),
          deactivated: !!props.deactivated
        })}>
        {avatarWithDetails}
      </Button>
      <Tooltip placement='top'>
        <div className='sm:max-w-[20vw] break-all'>
          <Tooltip.Title>{props.name ?? ''}</Tooltip.Title>
          <Tooltip.SupportiveText>{props.email ?? ''}</Tooltip.SupportiveText>
        </div>
      </Tooltip>
    </TooltipTrigger>
  ) : (
    avatarWithDetails
  )

  return (
    <div className='w-full' ref={containerRef} onClick={onClick}>
      {renderedAvatar}
    </div>
  )
}

export const CurrentUserAvatar = (props: AvatarProps) => {
  const { currentUser } = useOrgContext(state => ({
    currentUser: state.profile
  }))

  return (
    <Avatar
      {...props}
      imgUrl={currentUser!.img_url ?? undefined}
      label={currentUser!.name ?? currentUser!.email}
    />
  )
}

export default Avatar
